import React, { useState, useEffect } from "react";
import { Box, Typography, Grid, CircularProgress, Divider } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { ClearRounded, SaveRounded } from "@mui/icons-material";
import axios from 'axios';
import CustomButton from "../../common/CustomButton";
import CustomTextField from "../../common/CustomTextField";
import CustomTextarea from "../../common/CustomTextarea";
import CustomAutocomplete from "../../common/CustomAutoComplete";
import { READ_IMPORT_PLANNING_BUDGET, UPDATE_IMPORT_PLANNING_BUDGET, } from '../../../services/apiRoutes';
import CustomStyle from "../../common/CustomStyle";
import CustomPrefixSuffixField from "../../common/CustomPrefixSuffixField";
import { t } from "../../../constants/translationHelper";

const ValidationSchema = Yup.object().shape({
    seaFreight: Yup.string().required('Sea Freight is required'),
    currencyId: Yup.number().required('Currency is required'),
    cfsLocalCharges: Yup.number().required('CFS Local Charges are required'),
    shippingLineCharges: Yup.number().required('Shipping Line Charges are required'),
    blCharges: Yup.number().required('BL Charges are required'),


});

const UpdatePlanningBudgetExport = ({ handleClose, setMessage, fetchData, commercialInvoiceList, proformaInvoiceList, planningBudgetId, currencyList, clientCompanyId }) => {
    const [data, setData] = useState(null);
    const [currencySymbol, setCurrencySymbol] = useState(null);
    const [currencySym, setCurrencySym] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!planningBudgetId) return;

        const fetchData = async () => {
            try {
                const accessToken = sessionStorage.getItem('accessToken');
                const response = await axios.get(READ_IMPORT_PLANNING_BUDGET(planningBudgetId), {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });
                const currencySymbol = response?.data?.planningBudget?.baseCurrency?.currency_symbol || '';
                const currencySym = response?.data?.planningBudget?.currency?.currency_symbol || '';
                setCurrencySym(currencySym);
                setCurrencySymbol(currencySymbol);

                setData(response.data.planningBudget);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [planningBudgetId]);

    if (loading) return <CircularProgress />;
    if (error) return <p>Error: {error}</p>;

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            const accessToken = sessionStorage.getItem('accessToken');
            const response = await axios.put(UPDATE_IMPORT_PLANNING_BUDGET(planningBudgetId), values, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });

            if (response.status === 200) {
                setMessage({
                    open: true,
                    text: response?.data?.message || "Shipping Booking updated successfully",
                    type: "success",
                });
                fetchData();
                setTimeout(() => {
                    handleClose();
                }, 500);
            } else {
                alert('Something went wrong');
            }
        } catch (error) {
            alert('Something went wrong');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ mx: 'auto', mt: 0 }}>
            <Formik
                initialValues={{
                    shipmentOrgId: data.shipmentOrgId ?? '',
                    commanShipmentId: data.commanShipmentId ?? '',
                    shipmentId: data.shipmentId ?? '',
                    clientCompanyId: data.clientCompanyId ?? '',
                    currencyId: parseInt(data?.currencyId || ''),
                    baseCurrencyId: parseInt(data?.baseCurrencyId || ''),
                    conversionRate: data.conversionRate ?? '',
                    proformaInvoiceId: parseInt(data?.proformaInvoiceId || ''),
                    proformaInvExpId: parseInt(data?.proformaInvExpId || ''),
                    commercialInvExpId: parseInt(data?.commercialInvExpId || ''),
                    commercialInvoiceId: parseInt(data?.commercialInvoiceId || ''),
                    seaFreight: data.seaFreight ?? '',
                    clearingAgencyCharge: data.clearingAgencyCharge ?? '',
                    freightForwardAgencyCharge: data.freightForwardAgencyCharge ?? '',
                    cfsLocalCharges: data.cfsLocalCharges ?? '',
                    shippingLineCharges: data.shippingLineCharges ?? '',
                    blCharges: data.blCharges ?? '',
                    othersCharges: data.othersCharges ?? '',
                    dutyCharge: data.dutyCharge ?? '',
                    taxCharge: data.taxCharge ?? '',
                    transportCharge: data.transportCharge ?? ''

                }}
                validationSchema={ValidationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ isSubmitting, errors, touched, handleChange, setFieldValue, values }) => (
                    <Form>
                        <Grid container spacing={1} style={CustomStyle.container}>
                            <Grid item xs={4} md={4}>
                                <Typography sx={{ fontWeight: 600 }} style={CustomStyle.title}>
                                    {t("Update Planning & Budget")}
                                </Typography>
                            </Grid>
                            <Grid item xs={8} md={8} style={CustomStyle.buttonContainer}>
                                <Box style={CustomStyle.buttonBox}>
                                    <CustomButton
                                        startIcon={<SaveRounded />}
                                        title={isSubmitting ? <CircularProgress size={20} color="inherit" /> : "Save"}
                                        size="small"
                                        variant="contained"
                                        type="submit"
                                        disabled={isSubmitting}
                                    />
                                    <CustomButton
                                        startIcon={<SaveRounded />}
                                        title=" Save and New"
                                        size="small"
                                        variant="contained"
                                    />
                                    <CustomButton
                                        startIcon={<ClearRounded />}
                                        title="Cancel"
                                        size="small"
                                        variant="contained"
                                        onClick={handleClose}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                        <Divider />
                        <Grid container spacing={2} sx={{ px: "5%", paddingTop: "5px" }}>
                            <Grid item xs={12} md={6}>
                                <CustomAutocomplete
                                    options={proformaInvoiceList}
                                    fieldLabel="Proforma Invoice"
                                    getOptionLabel={(option) => option.title}
                                    value={proformaInvoiceList.find((option) => option.id === values.proformaInvExpId) || null}
                                    onChange={(e, newValue) =>
                                        setFieldValue('proformaInvExpId', newValue ? newValue.id : '')
                                    }
                                    placeholder="Select Proforma Invoice"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CustomAutocomplete
                                    options={commercialInvoiceList}
                                    fieldLabel="Commercial Invoice"
                                    getOptionLabel={(option) => option.title}
                                    value={commercialInvoiceList.find((option) => option.id === values.commercialInvExpId) || null}
                                    onChange={(e, newValue) =>
                                        setFieldValue('commercialInvExpId', newValue ? newValue.id : '')
                                    }
                                    placeholder="Select Commercial Invoice"
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <CustomAutocomplete
                                    options={currencyList}
                                    fieldLabel="Select Currency"
                                    required={true}
                                    getOptionLabel={(option) => option.title}
                                    value={currencyList.find((option) => option.id === values.currencyId) || null}
                                    onChange={(e, newValue) =>
                                        setFieldValue('currencyId', newValue ? newValue.id : '')
                                    }
                                    placeholder="Select Currency"
                                    error={touched.currencyId && !!errors.currencyId}
                                    helperText={touched.currencyId && errors.currencyId}
                                    sx={CustomStyle.readOnly}
                                    readOnly={true}

                                />
                            </Grid>

                            <Grid item xs={12} md={2}>
                                <CustomPrefixSuffixField
                                    prefix={currencySym}
                                    placeholder="Sea Freight"
                                    tagLine="Per Con."
                                    name="seaFreight"
                                    fieldLabel="Sea Freight"
                                    type="number"
                                    value={values.seaFreight}
                                    onChange={handleChange}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <CustomPrefixSuffixField
                                    prefix={currencySymbol}
                                    tagLine="(In Base Currency)"
                                    placeholder="Enter CFS Local Charges"
                                    name="cfsLocalCharges"
                                    fieldLabel="CFS/Local Charges"
                                    type="number"
                                    value={values.cfsLocalCharges}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CustomPrefixSuffixField
                                    prefix={currencySymbol}
                                    tagLine="(In Base Currency)"
                                    placeholder="Enter Shipping Line Charges"
                                    name="shippingLineCharges"
                                    fieldLabel="Shipping Line Charges"
                                    type="number"
                                    value={values.shippingLineCharges}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CustomPrefixSuffixField
                                    prefix={currencySymbol}
                                    tagLine="(In Base Currency)"
                                    placeholder="Enter BL Charges"
                                    name="blCharges"
                                    fieldLabel="BL Charges"
                                    type="number"
                                    value={values.blCharges}
                                    onChange={handleChange}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <CustomPrefixSuffixField
                                    prefix={currencySymbol}
                                    tagLine="(In Base Currency)"
                                    placeholder="Enter Conversion Rate"
                                    name="conversionRate"
                                    fieldLabel="Conversion Rate"
                                    type="number"
                                    value={values.conversionRate}
                                    onChange={handleChange}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <CustomPrefixSuffixField
                                    prefix={currencySymbol}
                                    tagLine="(In Base Currency)"
                                    placeholder="Enter Clearing Agency Charge"
                                    name="clearingAgencyCharge"
                                    fieldLabel="Clearing Agency Charge"
                                    type="number"
                                    value={values.clearingAgencyCharge}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CustomPrefixSuffixField
                                    prefix={currencySymbol}
                                    tagLine="(In Base Currency)"
                                    placeholder="Enter Freight Forward Agency Charge"
                                    name="freightForwardAgencyCharge"
                                    fieldLabel="Freight Forward Agency Charge"
                                    type="number"
                                    value={values.freightForwardAgencyCharge}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CustomPrefixSuffixField
                                    prefix={currencySymbol}
                                    tagLine="(In Base Currency)"
                                    placeholder="Enter Others Charges"
                                    name="othersCharges"
                                    fieldLabel="Others Charges"
                                    type="number"
                                    value={values.othersCharges}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CustomPrefixSuffixField
                                    prefix={currencySymbol}
                                    tagLine="(In Base Currency)"
                                    placeholder="Enter Duty Charge"
                                    name="dutyCharge"
                                    fieldLabel="Duty Charge"
                                    type="number"
                                    value={values.dutyCharge}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CustomPrefixSuffixField
                                    prefix={currencySymbol}
                                    tagLine="(In Base Currency)"
                                    placeholder="Enter Tax Charge"
                                    name="taxCharge"
                                    fieldLabel="Tax Charge"
                                    type="number"
                                    value={values.taxCharge}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CustomPrefixSuffixField
                                    prefix={currencySymbol}
                                    tagLine="(In Base Currency)"
                                    placeholder="Enter Transport Charge"
                                    name="transportCharge"
                                    fieldLabel="Transport Charge"
                                    type="number"
                                    value={values.transportCharge}
                                    onChange={handleChange}
                                />
                            </Grid>
                        </Grid>
                    </Form>
                )}
            </Formik>
        </Box>
    );
};

export default UpdatePlanningBudgetExport;

